import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import QRCode from 'qrcode';
import { connectPublico, connectVcardUrl } from '../api/apiConfig';
import { chileMobileCompleto, chileMobileDigits, formatChileMobile } from '../utils/phoneChile';
import { connectPublicUrl } from '../utils/connectUrls';
import { nfcSupported, writeNfcUrl } from '../utils/nfcWrite';
import logoInterfactor from '../assets/interfactor-wordmark.png';
import './ConnectTarjetaPublica.css';

const NEEDS = [
  'Información sobre factoring',
  'Liquidez',
  'Anticipar facturas',
  'Conocer alternativas',
  'Otro',
];

function splitName(full) {
  const parts = String(full || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: 'Interfactor', last: '' };
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] };
}

function sessionId() {
  const key = 'ifc_session';
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const id = crypto.randomUUID ? crypto.randomUUID() : `s_${Date.now()}`;
    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return `s_${Date.now()}`;
  }
}

function ConnectTarjetaPublica() {
  const { slug: slugParam } = useParams();
  const [params] = useSearchParams();
  const slug = (slugParam || params.get('c') || '').trim();
  const modoQr = params.get('modo') === 'qr';
  const [ejecutivo, setEjecutivo] = useState(null);
  const [status, setStatus] = useState('loading');
  const [formOpen, setFormOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [qrOpen, setQrOpen] = useState(modoQr);
  const [qrImg, setQrImg] = useState('');
  const [toast, setToast] = useState('');
  const [nfcMsg, setNfcMsg] = useState('');
  const [nfcBusy, setNfcBusy] = useState(false);
  const [phone, setPhone] = useState(() => formatChileMobile(''));
  const openedRef = useRef(false);
  const nfcAbortRef = useRef(null);
  const sid = useMemo(() => sessionId(), []);

  const source = (params.get('src') || ejecutivo?.origen_default || '').slice(0, 50);
  const { first, last } = splitName(ejecutivo?.nombre);
  const cardUrl = useMemo(() => {
    if (!ejecutivo?.slug) return '';
    return connectPublicUrl(window.location.origin, ejecutivo.slug, { src: source });
  }, [ejecutivo?.slug, source]);

  const payloadBase = useMemo(() => {
    if (!ejecutivo) return {};
    return {
      executive_id: ejecutivo.slug,
      ejecutivo_id: ejecutivo.slug,
      executive: ejecutivo.nombre,
      ejecutivo: ejecutivo.nombre,
      event_id: source,
      evento_id: source,
      event: source,
      evento: source,
      source,
      origen: source,
      session_id: sid,
      url: window.location.href,
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
    };
  }, [ejecutivo, source, sid, params]);

  const track = useCallback(async (tipo, extra = {}) => {
    if (!ejecutivo) return;
    try {
      await connectPublico({ accion: 'evento', tipo, type: tipo, ...payloadBase, ...extra });
    } catch {
      /* tracking no bloquea la UX */
    }
  }, [ejecutivo, payloadBase]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await connectPublico({ accion: 'tarjeta', slug });
        if (cancelled) return;
        if (!result?.ok || !result.ejecutivo) {
          setStatus('missing');
          return;
        }
        setEjecutivo(result.ejecutivo);
        setStatus('ok');
      } catch {
        if (!cancelled) setStatus('missing');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (status !== 'ok' || !ejecutivo || openedRef.current) return;
    openedRef.current = true;
    track('open');
    if (modoQr) setQrOpen(true);
  }, [status, ejecutivo, modoQr, track]);

  useEffect(() => {
    if (!cardUrl) return;
    let cancelled = false;
    QRCode.toDataURL(cardUrl, { width: 280, margin: 1, errorCorrectionLevel: 'M' })
      .then((data) => {
        if (!cancelled) setQrImg(data);
      })
      .catch(() => {
        if (!cancelled) setQrImg('');
      });
    return () => {
      cancelled = true;
    };
  }, [cardUrl]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 2300);
    return () => clearTimeout(t);
  }, [toast]);

  const saveContact = () => {
    if (!ejecutivo?.slug) return;
    track('save_contact');
    const vcfUrl = connectVcardUrl(ejecutivo.slug);
    const mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (mobile) {
      window.location.href = vcfUrl;
      return;
    }
    const filename = `${String(ejecutivo.nombre).replace(/\s+/g, '-')}-Interfactor.vcf`;
    fetch(vcfUrl)
      .then((res) => {
        if (!res.ok) throw new Error('vcard');
        return res.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1500);
      })
      .catch(() => {
        window.location.href = vcfUrl;
      });
  };

  const openWhatsapp = () => {
    if (!ejecutivo?.whatsapp) {
      setToast('Este ejecutivo no tiene teléfono en Gestión de Usuarios.');
      return;
    }
    track('whatsapp');
    const phone = String(ejecutivo.whatsapp).replace(/\D/g, '');
    const msg = encodeURIComponent(
      `Hola ${first}, nos conectamos a través de Interfactor Connect y me gustaría conversar sobre alternativas de liquidez para mi empresa.`,
    );
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank', 'noopener');
  };

  const openEmail = () => {
    if (!ejecutivo?.email) {
      setToast('Falta configurar el correo de este ejecutivo.');
      return;
    }
    track('email');
    window.location.href = `mailto:${ejecutivo.email}?subject=${encodeURIComponent('Contacto Interfactor')}`;
  };

  const share = async () => {
    track('share');
    if (!cardUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${ejecutivo.nombre} · Interfactor`,
          text: 'Te comparto mis datos de contacto.',
          url: cardUrl,
        });
        return;
      } catch {
        /* usuario canceló */
      }
    }
    try {
      if (window.isSecureContext && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(cardUrl);
        setToast('Enlace copiado');
        return;
      }
    } catch {
      /* fallback */
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = cardUrl;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;left:-9999px;top:0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      setToast(ok ? 'Enlace copiado' : 'No se pudo copiar el enlace');
    } catch {
      setToast('No se pudo copiar el enlace');
    }
  };

  const writeNfc = async () => {
    if (!cardUrl) return;
    if (!nfcSupported()) {
      setNfcMsg('Para grabar la etiqueta abre el kiosco en Chrome de un Android. El iPhone del cliente igual abre la tarjeta al acercarlo.');
      return;
    }
    nfcAbortRef.current?.abort();
    const ac = new AbortController();
    nfcAbortRef.current = ac;
    setNfcBusy(true);
    setNfcMsg('Acerca la etiqueta al teléfono…');
    try {
      await writeNfcUrl(cardUrl, { signal: ac.signal });
      setNfcMsg('Etiqueta grabada. El cliente solo tiene que acercar el celular.');
      track('nfc_write');
    } catch (e) {
      if (e?.name === 'AbortError') return;
      const denied = /notallowed|secure|user/i.test(String(e?.name || e?.message || ''));
      setNfcMsg(
        denied
          ? 'Chrome pidió permiso para NFC. Acepta y vuelve a intentar.'
          : 'No se pudo grabar. Usa una etiqueta regrabable y mantenla cerca.',
      );
    } finally {
      setNfcBusy(false);
    }
  };

  useEffect(() => {
    if (qrOpen) return undefined;
    nfcAbortRef.current?.abort();
    setNfcBusy(false);
    return undefined;
  }, [qrOpen]);

  const submitLead = async (ev) => {
    ev.preventDefault();
    const form = new FormData(ev.currentTarget);
    if (form.get('website')) return;
    if (!chileMobileCompleto(phone)) {
      setToast('Ingresa un celular de 8 dígitos (56 9 XXXX XXXX).');
      return;
    }
    setSending(true);
    try {
      const leadId = crypto.randomUUID ? crypto.randomUUID() : `l_${Date.now()}`;
      await connectPublico({
        accion: 'lead',
        lead_id: leadId,
        name: form.get('name'),
        company: form.get('company'),
        phone: chileMobileDigits(phone),
        email: form.get('email'),
        need: form.get('need'),
        consent: form.get('consent') ? 'SI' : 'NO',
        website: form.get('website') || '',
        ...payloadBase,
      });
      await track('lead_submit', { lead_id: leadId });
      setSent(true);
    } catch (e) {
      setToast(e?.message || 'No se pudo registrar. Intenta de nuevo.');
    } finally {
      setSending(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="ifc-root">
        <div className="ifc-empty">Cargando tarjeta…</div>
      </div>
    );
  }

  if (status === 'missing' || !ejecutivo) {
    return (
      <div className="ifc-root">
        <div className="ifc-empty">
          <img src={logoInterfactor} alt="Interfactor" className="ifc-logo" style={{ margin: '0 auto 18px' }} />
          <h1 style={{ fontSize: 28, margin: '0 0 8px' }}>Tarjeta no disponible</h1>
          <p>Este enlace no corresponde a un ejecutivo activo de Interfactor Connect.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ifc-root">
      <div className="ifc-shell">
        <div className="ifc-curve" />
        <div className="ifc-curve two" />
        <header className="ifc-header">
          <img className="ifc-logo" src={logoInterfactor} alt="Interfactor" />
          <span className="ifc-mode">CONECTADO</span>
        </header>
        <main>
          <section className="ifc-hero">
            <div className="ifc-avatar" aria-label={ejecutivo.nombre}>
              {ejecutivo.iniciales || 'IF'}
            </div>
            <h1>
              {first} {last ? <strong>{last}</strong> : null}
            </h1>
            <div className="ifc-role">{ejecutivo.cargo || 'Ejecutivo Comercial'} · Interfactor</div>
            <p className="ifc-tagline">
              Soluciones de liquidez para que tu empresa <strong>siga avanzando.</strong>
            </p>
          </section>
          <section className="ifc-actions">
            <button
              className="ifc-btn ifc-primary"
              type="button"
              onClick={() => {
                setFormOpen(true);
                setSent(false);
                setPhone(formatChileMobile(''));
                track('connect_open');
              }}
            >
              ↗ CONECTEMOS
            </button>
            <div className="ifc-grid2">
              <button className="ifc-btn ifc-secondary" type="button" onClick={saveContact}>
                ＋ Guardar contacto
              </button>
              <button className="ifc-btn ifc-secondary" type="button" onClick={openWhatsapp}>
                ◉ WhatsApp
              </button>
            </div>
          </section>
          <section className="ifc-quick">
            <button type="button" onClick={share}>
              <span className="ifc-ico">⇧</span>Compartir
            </button>
            <button
              type="button"
              onClick={() => {
                setQrOpen(true);
                track('show_qr');
              }}
            >
              <span className="ifc-ico">▦</span>Mostrar QR
            </button>
            <button type="button" onClick={openEmail}>
              <span className="ifc-ico">✉</span>Correo
            </button>
          </section>
          <section className="ifc-card">
            <h2>Conectemos en segundos</h2>
            <p>Escanea, guarda el contacto o déjanos tus datos. Sin instalar aplicaciones y con seguimiento del origen comercial.</p>
            <div className="ifc-status">
              <span className="ifc-dot" />
              <span>Registro de contacto seguro y directo con Interfactor.</span>
            </div>
          </section>
        </main>
        <footer className="ifc-footer">
          <b>Interfactor Connect</b> · Financiamiento PYME
        </footer>
      </div>

      <div
        className={`ifc-backdrop${formOpen ? ' open' : ''}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) setFormOpen(false);
        }}
      >
        <section className="ifc-sheet">
          <div className="ifc-handle" />
          {!sent ? (
            <div>
              <div className="ifc-sheet-head">
                <div>
                  <h2>¿Conversamos?</h2>
                  <p style={{ margin: '5px 0 0', color: '#6b7a83', fontSize: 13 }}>Déjame tus datos y te contacto.</p>
                </div>
                <button className="ifc-close" type="button" aria-label="Cerrar" onClick={() => setFormOpen(false)}>
                  ×
                </button>
              </div>
              <form className="ifc-form" onSubmit={submitLead}>
                <label>
                  Nombre *
                  <input name="name" required autoComplete="name" maxLength={80} placeholder="Tu nombre" />
                </label>
                <label>
                  Empresa *
                  <input name="company" required autoComplete="organization" maxLength={100} placeholder="Nombre de tu empresa" />
                </label>
                <label>
                  WhatsApp *
                  <input
                    name="phone"
                    required
                    autoComplete="tel"
                    inputMode="tel"
                    maxLength={14}
                    value={phone}
                    onChange={(e) => setPhone(formatChileMobile(e.target.value))}
                    onFocus={() => setPhone((p) => formatChileMobile(p))}
                    placeholder="56 9 1234 5678"
                  />
                </label>
                <label>
                  Correo
                  <input name="email" type="email" autoComplete="email" maxLength={120} placeholder="nombre@empresa.cl" />
                </label>
                <label>
                  ¿Qué necesitas?
                  <select name="need" defaultValue={NEEDS[0]}>
                    {NEEDS.map((n) => (
                      <option key={n}>{n}</option>
                    ))}
                  </select>
                </label>
                <label className="ifc-hp">
                  Sitio web
                  <input name="website" tabIndex={-1} autoComplete="off" />
                </label>
                <label className="ifc-consent">
                  <input type="checkbox" name="consent" required />
                  <span>Autorizo a Interfactor a utilizar estos datos para contactarme respecto de sus productos y servicios.</span>
                </label>
                <button className="ifc-btn ifc-primary" type="submit" disabled={sending}>
                  {sending ? 'REGISTRANDO…' : 'ENVIAR Y CONECTAR'}
                </button>
              </form>
            </div>
          ) : (
            <div className="ifc-success">
              <div className="ifc-check">✓</div>
              <h3>¡Ya estamos conectados!</h3>
              <p>
                Tus datos fueron registrados. Puedes guardar a {first} o escribirle directamente.
              </p>
              <div className="ifc-grid2">
                <button className="ifc-btn ifc-secondary" type="button" onClick={saveContact}>
                  ＋ Guardar a {first}
                </button>
                <button className="ifc-btn ifc-primary" type="button" onClick={openWhatsapp}>
                  ◉ WhatsApp
                </button>
              </div>
              <button
                className="ifc-btn ifc-secondary"
                style={{ width: '100%', marginTop: 9 }}
                type="button"
                onClick={() => {
                  setFormOpen(false);
                  setSent(false);
                }}
              >
                Listo
              </button>
            </div>
          )}
        </section>
      </div>

      <div className={`ifc-qr${qrOpen ? ' open' : ''}`}>
        <div className="ifc-qr-wrap">
          <img className="ifc-qr-logo" src={logoInterfactor} alt="Interfactor" />
          <div className="ifc-qr-box">
            {qrImg ? <img src={qrImg} alt={`QR Interfactor Connect para ${ejecutivo.nombre}`} /> : <p style={{ color: '#253746' }}>Generando QR…</p>}
          </div>
          <h2>Conectemos</h2>
          <p>
            Pídele al potencial cliente que escanee este código o acerque el celular a la etiqueta NFC. Abrirá la tarjeta de {first} y registrará el origen automáticamente.
          </p>
          <button className="ifc-nfc" type="button" onClick={writeNfc} disabled={nfcBusy || !cardUrl}>
            {nfcBusy ? 'Esperando etiqueta…' : 'Grabar etiqueta NFC'}
          </button>
          {nfcMsg ? <p className="ifc-nfc-msg">{nfcMsg}</p> : null}
          <button className="ifc-qr-close" type="button" onClick={() => setQrOpen(false)}>
            Cerrar QR
          </button>
          {source ? (
            <div className="ifc-source">
              ORIGEN · <span>{source}</span>
            </div>
          ) : null}
        </div>
      </div>
      {toast ? <div className="ifc-toast">{toast}</div> : null}
    </div>
  );
}

export default ConnectTarjetaPublica;
